// Datas no horário LOCAL do usuário (due_date é uma data sem fuso — spec, Modelo de dados).

export type DateKey = string; // YYYY-MM-DD

const pad = (n: number) => String(n).padStart(2, '0');

export function toDateKey(d: Date): DateKey {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function parseDateKey(key: DateKey): Date {
  const [y, m, d] = key.split('-').map(Number);
  return new Date(y, m - 1, d);
}

export function addDays(key: DateKey, days: number): DateKey {
  const d = parseDateKey(key);
  d.setDate(d.getDate() + days);
  return toDateKey(d);
}

/** Diferença em dias de calendário (b − a). */
export function diffDays(a: DateKey, b: DateKey): number {
  const ms = parseDateKey(b).getTime() - parseDateKey(a).getTime();
  return Math.round(ms / 86_400_000);
}

export function isValidDateKey(key: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(key)) return false;
  return toDateKey(parseDateKey(key)) === key;
}

/** "HH:MM" (aceita "HH:MM:SS" vindo do Postgres e corta os segundos). */
export function normalizeTime(value: string): string | null {
  const m = /^(\d{2}):(\d{2})(?::\d{2})?$/.exec(value);
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h > 23 || min > 59) return null;
  return `${m[1]}:${m[2]}`;
}

/**
 * Instante do prazo. Só data → fim daquele dia, 23:59:59.999 (ASM-001, confirmada).
 */
export function dueInstant(dueDate: DateKey, dueTime: string | null): Date {
  const d = parseDateKey(dueDate);
  if (dueTime) {
    const [h, m] = dueTime.split(':').map(Number);
    d.setHours(h, m, 0, 0);
  } else {
    d.setHours(23, 59, 59, 999);
  }
  return d;
}

/** "14:30" → "2:30 PM" (12h) ou "14:30" (24h). */
export function formatTime(time: string, format: '12h' | '24h'): string {
  if (format === '24h') return time;
  const [h, m] = time.split(':').map(Number);
  const suffix = h >= 12 ? 'PM' : 'AM';
  return `${h % 12 === 0 ? 12 : h % 12}:${String(m).padStart(2, '0')} ${suffix}`;
}

/**
 * Lê o que a pessoa digitou no campo de horário e devolve "HH:MM" (ou null se inválido).
 * 24h: "1430", "14:30", "9" → "09:00". 12h: usa `meridiem` (ou "pm"/"am" digitado).
 */
export function parseTimeText(text: string, format: '12h' | '24h', meridiem: 'AM' | 'PM' = 'AM'): string | null {
  let s = text.trim().toLowerCase().replace(/\s+/g, '');
  if (!s) return null;
  let mer: 'AM' | 'PM' = meridiem;
  const tail = /(a|p)\.?m?\.?$/.exec(s);
  if (tail) {
    mer = tail[1] === 'p' ? 'PM' : 'AM';
    s = s.slice(0, tail.index);
  }
  let h: number;
  let m: number;
  const colon = /^(\d{1,2})[:h.](\d{1,2})?$/.exec(s);
  const digits = /^(\d{1,4})$/.exec(s);
  if (colon) {
    h = Number(colon[1]);
    m = colon[2] ? Number(colon[2]) : 0;
  } else if (digits) {
    const d = digits[1];
    if (d.length <= 2) {
      h = Number(d);
      m = 0;
    } else {
      h = Number(d.slice(0, d.length - 2));
      m = Number(d.slice(-2));
    }
  } else return null;
  if (m > 59) return null;
  if (format === '12h' || tail) {
    if (h < 1 || h > 12) return null;
    h = (h % 12) + (mer === 'PM' ? 12 : 0);
  } else if (h > 23) return null;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}
